import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

interface ErrorDisplayProps {
  error?: Error;
}

function ErrorDisplay({ error }: ErrorDisplayProps) {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center border border-slate-200">
        <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-700" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          {t("common.errorTitle")}
        </h2>
        <p className="text-slate-600 mb-6">
          {t("common.errorMessage")}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-md transition-smooth"
        >
          {t("common.refreshPage")}
        </button>
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-slate-500 mb-2">
              {t("admin.errorDetails")}
            </summary>
            <pre className="bg-slate-50 p-4 rounded-xl text-xs overflow-auto border border-slate-200">
              {error.toString()}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // Error is handled by getDerivedStateFromError and displayed in render
    // In production, you might want to log this to an error reporting service
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorDisplay error={this.state.error} />;
    }

    return this.props.children;
  }
}

